import { useState } from 'react'
import { useAuth } from 'app/frontend/base/config/context/auth'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Input } from '@/components/ui/input'
import { Plus } from 'lucide-react'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import capitalize from 'lodash/capitalize'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { cn } from '@/lib/utils'
import { useTeamMemberEditor } from '../hooks'
import {
  RankMenuItems,
  MainSpecMenuItems,
  OffSpecMenuItems,
} from './TeamMemberEditMenus'

type CellProps = {
  member: wowaudit.TeamMembership
  index: number
}

export const NoteCell = ({ member, index }: CellProps) => {
  const { accessLevel } = useAuth()
  const isAdmin = accessLevel === 'admin'
  const { handleNoteChange } = useTeamMemberEditor(member)
  const [editing, setEditing] = useState(false)
  const [displayNote, setDisplayNote] = useState(member.note ?? '')
  const [value, setValue] = useState('')

  const startEditing = () => {
    setValue(displayNote)
    setEditing(true)
  }

  const commitEdit = () => {
    setDisplayNote(value)
    handleNoteChange(value)
    setEditing(false)
  }

  if (editing && isAdmin) {
    return (
      <div
        className='h-6 flex items-center'
        onClick={(e) => e.stopPropagation()}
      >
        <Input
          autoFocus
          className='h-6 text-xs py-0 px-1'
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitEdit()
            }
            if (e.key === 'Escape') {
              setEditing(false)
            }
          }}
        />
      </div>
    )
  }

  if (!displayNote && isAdmin) {
    return (
      <div
        className='h-6 flex items-center cursor-pointer'
        onClick={startEditing}
      >
        <Plus
          size={index === 0 ? 14 : 12}
          className='text-muted-foreground/40'
        />
      </div>
    )
  }

  if (!displayNote) {
    return <div className='h-6' />
  }

  const textDiv = (
    <div
      className={cn(
        'h-6 flex items-center overflow-hidden max-w-[160px]',
        isAdmin && 'cursor-pointer',
      )}
      onClick={isAdmin ? startEditing : undefined}
    >
      <span
        className={cn(
          'truncate',
          index !== 0 && 'text-muted-foreground text-xs',
        )}
      >
        {displayNote}
      </span>
    </div>
  )

  if (displayNote.length < 16 && !isAdmin) return textDiv

  if (displayNote.length < 16) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{textDiv}</TooltipTrigger>
        <TooltipContent>
          <p className='text-muted-foreground text-xs'>Click to edit</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{textDiv}</TooltipTrigger>
      <TooltipContent className='max-w-90 flex flex-col gap-1 items-center justify-center'>
        <p className='whitespace-normal text-center'>{displayNote}</p>
        {isAdmin && (
          <p className='text-muted-foreground text-xs'>Click to edit</p>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

export const EditableRankCell = ({ member, index }: CellProps) => {
  const { accessLevel } = useAuth()
  const { handleRankChange } = useTeamMemberEditor(member)
  const isOrphanAlt = !!member.teamRank.for_alts

  const display = (
    <span
      className={cn(
        'h-6 text-xs flex items-center',
        !isOrphanAlt && index === 0 && 'text-sm',
        member.teamRank.color ? '' : 'text-muted-foreground',
        !isOrphanAlt &&
          index === 0 &&
          !member.teamRank.color &&
          'text-foreground',
        accessLevel === 'admin' && 'cursor-pointer',
      )}
      style={{ color: member.teamRank.color }}
    >
      {member.teamRank.name}
    </span>
  )

  if (accessLevel !== 'admin') return display

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{display}</DropdownMenuTrigger>
      <DropdownMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
        <RankMenuItems member={member} onRankChange={handleRankChange} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const EditableRoleCell = ({ member, index }: CellProps) => {
  const { accessLevel } = useAuth()
  const {
    handleRoleChange,
    handleSpecChange,
    classSpecializations,
    rolesWithMultipleSpecs,
  } = useTeamMemberEditor(member)
  const isOrphanAlt = !!member.teamRank.for_alts

  const display = (
    <span
      className={cn(
        'flex gap-2 items-center h-6 text-xs',
        !isOrphanAlt && index === 0 && 'text-sm text-foreground',
        accessLevel === 'admin' && 'cursor-pointer',
      )}
    >
      <CharacterRoleIcon
        role={member.role}
        className={index === 0 ? '' : 'opacity-70'}
        size={index === 0 ? 'medium' : 'small'}
      />
      {capitalize(member.role)}
    </span>
  )

  if (accessLevel !== 'admin') return display

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{display}</DropdownMenuTrigger>
      <DropdownMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
        <MainSpecMenuItems
          member={member}
          classSpecializations={classSpecializations}
          rolesWithMultipleSpecs={rolesWithMultipleSpecs}
          onRoleChange={handleRoleChange}
          onSpecChange={handleSpecChange}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const EditableClassCell = ({ member, index }: CellProps) => {
  const { accessLevel } = useAuth()
  const {
    handleRoleChange,
    handleSpecChange,
    classSpecializations,
    rolesWithMultipleSpecs,
  } = useTeamMemberEditor(member)
  const isOrphanAlt = !!member.teamRank.for_alts

  const display = (
    <span
      className={cn(
        'flex gap-2 items-center h-6 text-xs text-muted-foreground',
        !isOrphanAlt && index === 0 && 'text-sm text-foreground',
        accessLevel === 'admin' && 'cursor-pointer',
      )}
    >
      {member.specDetails ? (
        <BlizzardIcon
          iconName={member.specDetails.icon}
          className={index === 0 ? '' : 'opacity-70'}
          size={index === 0 ? 'medium' : 'small'}
        />
      ) : (
        <CharacterClassIcon
          characterClass={member.characterReference.characterClass}
          className={index === 0 ? '' : 'opacity-70'}
          size={index === 0 ? 'medium' : 'small'}
        />
      )}
      <span
        className={classColours[member.characterReference.characterClass].text}
      >
        {member.specDetails?.name} {member.characterReference.characterClass}
      </span>
    </span>
  )

  if (accessLevel !== 'admin') return display

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{display}</DropdownMenuTrigger>
      <DropdownMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
        <MainSpecMenuItems
          member={member}
          classSpecializations={classSpecializations}
          rolesWithMultipleSpecs={rolesWithMultipleSpecs}
          onRoleChange={handleRoleChange}
          onSpecChange={handleSpecChange}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const EditableOffSpecsCell = ({ member, index }: CellProps) => {
  const { accessLevel } = useAuth()
  const { handleOffSpecChange, classSpecializations } =
    useTeamMemberEditor(member)
  const isAdmin = accessLevel === 'admin'
  const hasOffSpecs = member.offspecDetails.length > 0

  const iconsDisplay = (
    <span className='h-6 text-xs first:text-sm flex items-center gap-1'>
      {member.offspecDetails.map((spec) => (
        <Tooltip key={spec.name}>
          <TooltipTrigger>
            <BlizzardIcon
              iconName={spec.icon}
              className={index === 0 ? '' : 'opacity-70'}
              size={index === 0 ? 'medium' : 'small'}
            />
          </TooltipTrigger>
          <TooltipContent>{spec.name}</TooltipContent>
        </Tooltip>
      ))}
    </span>
  )

  if (!isAdmin) return iconsDisplay

  if (!hasOffSpecs) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <span className='h-6 flex items-center cursor-pointer'>
            <Plus
              size={index === 0 ? 14 : 12}
              className='text-muted-foreground/40'
            />
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
          <OffSpecMenuItems
            member={member}
            classSpecializations={classSpecializations}
            onOffSpecChange={handleOffSpecChange}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <span className='cursor-pointer'>{iconsDisplay}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
        <OffSpecMenuItems
          member={member}
          classSpecializations={classSpecializations}
          onOffSpecChange={handleOffSpecChange}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
